// SU structure loader — v0M1.9.16.
//
// Loads the 6 basic v1 structures from src/data/generated/dials.json
// (produced by scripts/convert-xlsx.ts against docs/Balance.xlsx). Every
// structure produces exactly one resource type at a fixed rate per second
// per ECONOMY.md §3.1.

import { assertSchemaVersion, BALANCE_SCHEMA_VERSION } from './loader';
import rawBlob from './generated/dials.json';

export interface StructureSpec {
  /**
   * Resource this structure produces (e.g. 'Iron', 'Supplies', 'Gold').
   * MFix9 (2026-07-24): Food merged into Supplies. Farm now produces
   * Supplies; Market now produces Gold. Non-producing structures (Walls,
   * Barracks, etc.) leave this blank.
   */
  readonly produces: string;
  /** Production rate in resource units per second. 0 for non-producing structures. */
  readonly ratePerSec: number;
  /** Gold cost to build (uniform 100 at v0M1.9.16). */
  readonly goldCost: number;
  /** Build time in seconds (uniform 60 at v0M1.9.16). */
  readonly buildTimeSec: number;
  /**
   * MFix9 (2026-07-24) — free-text designer effects, e.g. "Unlocks Cohort
   * Recruitment", "+1 recruit cap". Informational at v1 — per-effect
   * runtime consumers land post-MVP as each structure's mechanic is wired.
   */
  readonly effects: string;
  /**
   * MFix12.3 (2026-07-27) — player-facing free-text description of what
   * the structure conceptually does. Shown above the produces/cost rows
   * in the settlement Build tab. Optional in xlsx (back-compat); empty
   * string or undefined is treated as absent and the UI falls back to
   * `effects` / produces text.
   */
  readonly description?: string;
  /** Free-text notes from the xlsx (design rationale, playtest flags, etc.). */
  readonly notes: string;
}

export type Structures = Readonly<Record<string, StructureSpec>>;

/** Load the 6 basic structures. Fails loudly on schemaVersion mismatch. */
export function loadStructures(): Structures {
  assertSchemaVersion(rawBlob, BALANCE_SCHEMA_VERSION, 'src/data/generated/dials.json');
  return (rawBlob as unknown as { structures: Structures }).structures;
}

/** Retrieve a structure by name; throw on unknown names. */
export function getStructure(structures: Structures, name: string): StructureSpec {
  const entry = structures[name];
  if (!entry) {
    throw new Error(`Unknown structure: ${name}. Known: ${Object.keys(structures).join(', ')}`);
  }
  return entry;
}
